import { FC, Fragment, ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';

import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import { useTrackLoadingState } from 'common/sentry/webAppOpeningTransaction';
import Button from 'components/_core/Button';
import Text from 'components/_core/Text';
import Loader from 'components/Loader';
import {
  defaults as defaultsFilters,
  useTransactionsFilters,
} from 'features/Bank/pages/Overview/hooks/useTransactionsFilters';
import { formatCalendar } from 'helpers/date';
import { capitalizeFirstLetter } from 'helpers/string';

import NoResult from './components/NoResult';
import locales from './locales';
import TransactionItem from './TransactionItem';
import {
  TransactionListProps,
  TransactionNode,
  TransactionSection,
} from './types';

const TransactionList: FC<TransactionListProps> = ({
  hasMoreResults,
  isFetchingMore,
  loading,
  loadMore,
  sections,
}) => {
  const { updateFilters } = useTransactionsFilters();
  const { bankAccounts } = useCompanyBankAccounts({
    includeClosedBankAccounts: true,
  });

  const bankAccountIllustrations = bankAccounts.reduce(
    (acc, { bankAccountId, illustrationName }) => ({
      ...acc,
      [bankAccountId]: illustrationName ?? 'wallet',
    }),
    {},
  );

  /**
   * Track this page as being fully loaded.
   * @warning please ensure all loaders that impact the rendering of the meaningful content of this page are provided to this hook.
   */
  useTrackLoadingState({
    destination: 'transaction-list',
    // seems unintuitive, but a first-time user won't see the <Loader /> below and still have 0 section, so this condition seems fine.
    isLoaded: !loading || sections.length > 0,
  });

  if (loading && sections.length === 0) {
    return <Loader />;
  }

  if (sections.length === 0) {
    return <NoResult onResetFilters={() => updateFilters(defaultsFilters)} />;
  }

  return (
    <Flex direction="column">
      {sections.map(
        ({ data, key }: TransactionSection): ReactNode => (
          <Fragment key={key}>
            <Text
              fontWeight="weight-500"
              marginTop="space-32"
              paddingX="space-16"
              variant="light"
            >
              {key === 'PENDING'
                ? locales.pending
                : capitalizeFirstLetter(formatCalendar(new Date(key)))}
            </Text>

            <Box>
              {data.map(
                (transaction: TransactionNode): ReactNode => (
                  <TransactionItem
                    bankAccountIllustrations={bankAccountIllustrations}
                    key={transaction.transactionId}
                    transaction={transaction}
                  />
                ),
              )}
            </Box>
          </Fragment>
        ),
      )}

      {hasMoreResults ? (
        <Button
          alignSelf="center"
          isLoading={isFetchingMore}
          marginY="space-32"
          onClick={loadMore}
          variant="secondary"
        >
          {locales.seeMore}
        </Button>
      ) : null}
    </Flex>
  );
};

export default TransactionList;
